import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  ogTitle?: string | undefined;
  ogDescription?: string | undefined;
  ogImage?: string | undefined;
  ogType?: string | undefined;
  canonicalUrl?: string | undefined;
  twitterCard?: 'summary' | 'summary_large_image' | undefined;
  keywords?: string[] | undefined;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | undefined;
  /** When true, ask search engines not to index this URL. */
  noindex?: boolean;
}

/**
 * Hook to dynamically update document head meta tags for SEO
 * Updates title, description, Open Graph, and Twitter Card meta tags
 */
export const useSEO = ({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  canonicalUrl,
  twitterCard = 'summary_large_image',
  keywords,
  jsonLd,
  noindex = false,
}: SEOProps) => {
  useEffect(() => {
    // Store original values to restore on unmount
    const originalTitle = document.title;
    const originalMetas: Map<string, string | null> = new Map();

    const setMeta = (property: string, content: string, isName = false) => {
      const selector = isName ? `meta[name="${property}"]` : `meta[property="${property}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement | null;
      
      if (!element) {
        element = document.createElement('meta');
        if (isName) {
          element.name = property;
        } else {
          element.setAttribute('property', property);
        }
        document.head.appendChild(element);
        originalMetas.set(selector, null); // Mark as newly created
      } else {
        originalMetas.set(selector, element.content);
      }
      
      element.content = content;
    };

    // Update title
    document.title = title;

    // Update meta description
    setMeta('description', description, true);

    // Keywords
    if (keywords && keywords.length > 0) {
      setMeta('keywords', keywords.join(', '), true);
    }

    // Open Graph tags
    setMeta('og:title', ogTitle || title);
    setMeta('og:description', ogDescription || description);
    setMeta('og:type', ogType);
    
    if (ogImage) {
      setMeta('og:image', ogImage);
    }

    // Every indexable page points search engines at itself. Without an explicit
    // URL, fall back to the current path on the production domain so no page
    // inherits the homepage canonical from index.html.
    const selfPath = window.location.pathname.replace(/\/+$/, '') || '/';
    // On /insights?post=… the Overrank embed owns the canonical (full article URL).
    const overrankArticle = selfPath === '/insights' && new URLSearchParams(window.location.search).has('post');
    const effectiveCanonical = overrankArticle ? undefined : canonicalUrl || (noindex ? undefined : `https://getamorette.com${selfPath}`);
    if (effectiveCanonical) {
      setMeta('og:url', effectiveCanonical);
      document.querySelectorAll('link[rel="canonical"]').forEach((el, i) => { if (i > 0) el.remove(); });
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = effectiveCanonical;
    }

    // Indexing directive
    setMeta('robots', noindex ? 'noindex, follow' : 'index, follow', true);

    // Twitter Card tags
    setMeta('twitter:card', twitterCard, true);
    setMeta('twitter:title', ogTitle || title, true);
    setMeta('twitter:description', ogDescription || description, true);
    
    if (ogImage) {
      setMeta('twitter:image', ogImage, true);
    }

    // JSON-LD structured data
    let jsonLdScript: HTMLScriptElement | null = null;
    if (jsonLd) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.type = 'application/ld+json';
      jsonLdScript.text = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdScript);
    }

    // Cleanup function to restore original values
    return () => {
      document.title = originalTitle;
      
      originalMetas.forEach((originalContent, selector) => {
        const element = document.querySelector(selector) as HTMLMetaElement | null;
        if (element) {
          if (originalContent === null) {
            // Was newly created, remove it
            element.remove();
          } else {
            // Restore original content
            element.content = originalContent;
          }
        }
      });

      // Remove JSON-LD script
      if (jsonLdScript) {
        jsonLdScript.remove();
      }
    };
  }, [title, description, ogTitle, ogDescription, ogImage, ogType, canonicalUrl, twitterCard, keywords, jsonLd, noindex]);
};

export default useSEO;
